import type { ParallaxElementConfig } from 'parallax-controller'

/**
 * Default CMS offset → translate magnitude map, in % of the parallax element's own height.
 * Derived from the legacy px map `{ 1: 50, ..., 5: 125 }` at a ~700px-tall reference banner,
 * so typical banners keep their legacy feel while other heights scale proportionally.
 * @category Utils
 */
export const DEFAULT_PARALLAX_OFFSET_MAP: Record<number, number> = {
  0: 0,
  1: 7,
  2: 10,
  3: 12,
  4: 14,
  5: 18,
}

/**
 * Builds a vertical translate parallax config and derived height offset from an offset map.
 *
 * The element must overshoot its clipping container so the media keeps covering it while it
 * moves. `height: calc(100% + translate)` (centered) only covers half the travel and can leave
 * gaps near the scroll extremes; `CmsBaseParallaxMedia` implements the exact-coverage variant.
 * @param offset - The CMS offset value; `0`, `undefined`, and `null` disable parallax.
 * @param offsetMap - Map of `|offset|` → translate magnitude, interpreted in `unit`.
 * @param unit - Unit of the `offsetMap` values; defaults to `%` for the shared default map and to
 * `px` for custom maps (all pre-existing custom maps are px-based).
 * @category Utils
 */
export function getOffsetParallaxConfig(
  offset: number | undefined,
  offsetMap: Record<number, number> = DEFAULT_PARALLAX_OFFSET_MAP,
  unit: 'px' | '%' = offsetMap === DEFAULT_PARALLAX_OFFSET_MAP ? '%' : 'px'
): { config: ParallaxElementConfig | null, translate: string, speed: number } {
  const speed = getParallaxSpeed(offset, offsetMap) || 0

  return {
    config: offset
      ? {
          translateY: [`${speed}${unit}`, `${-speed}${unit}`],
        }
      : null,
    translate: `${Math.abs(speed)}${unit}`,
    speed,
  }
}
